import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, AlertTriangle, Mail, MessageCircle, Search, 
  RefreshCw, ExternalLink, Users, Building 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeliveryFailures = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [failures, setFailures] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    days: '30',
    channel: 'all',
    magazine: 'all'
  });

  const fetchFailures = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/delivery-failures`, {
        days: parseInt(filters.days),
        channel: filters.channel,
        magazine: filters.magazine
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFailures(response.data.failures || []);
      setSummary(response.data.summary || null);
      
      if (response.data.failures?.length === 0) {
        toast.info('No delivery failures found for the selected criteria');
      } else {
        toast.success(`Found ${response.data.failures?.length} delivery failures`);
      }
    } catch (error) {
      console.error('Error fetching failures:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch delivery failures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFailures();
  }, []);

  const handleNavigateToEntity = (failure) => {
    if (!failure.entity_id) {
      toast.warning('Entity ID not available');
      return;
    }
    
    if (failure.entity_type === 'Member') {
      navigate(`/search?member_id=${failure.entity_id}`);
    } else if (failure.entity_type === 'Advertiser') {
      navigate(`/search-advertiser?advertiser_id=${failure.entity_id}`);
    } else {
      toast.warning('Unknown entity type');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>
              <h1 className="text-3xl font-bold text-red-700" data-testid="page-title">
                <AlertTriangle className="inline mr-3" size={32} />
                Delivery Failures Report
              </h1>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <Label>Time Range</Label>
                <Select 
                  value={filters.days} 
                  onValueChange={(v) => setFilters({ ...filters, days: v })}
                >
                  <SelectTrigger data-testid="days-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="60">Last 60 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Channel</Label>
                <Select 
                  value={filters.channel} 
                  onValueChange={(v) => setFilters({ ...filters, channel: v })}
                >
                  <SelectTrigger data-testid="channel-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Channels</SelectItem>
                    <SelectItem value="email">Email Only</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Magazine</Label>
                <Select 
                  value={filters.magazine} 
                  onValueChange={(v) => setFilters({ ...filters, magazine: v })}
                >
                  <SelectTrigger data-testid="magazine-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Magazines</SelectItem>
                    <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                    <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={fetchFailures} 
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
                data-testid="search-button"
              >
                {loading ? (
                  <RefreshCw className="animate-spin mr-2" size={18} />
                ) : (
                  <Search className="mr-2" size={18} />
                )}
                Search
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
              <div className="bg-red-100 p-4 rounded-lg text-center">
                <p className="text-3xl font-bold text-red-700">{summary.total_failures}</p>
                <p className="text-sm text-red-600">Total Failures</p>
              </div>
              <div className="bg-blue-100 p-4 rounded-lg text-center">
                <Mail className="inline mb-1 text-blue-600" size={20} />
                <p className="text-2xl font-bold text-blue-700">{summary.email_failures}</p>
                <p className="text-sm text-blue-600">Email</p>
              </div>
              <div className="bg-green-100 p-4 rounded-lg text-center">
                <MessageCircle className="inline mb-1 text-green-600" size={20} />
                <p className="text-2xl font-bold text-green-700">{summary.whatsapp_failures}</p>
                <p className="text-sm text-green-600">WhatsApp</p>
              </div>
              <div className="bg-purple-100 p-4 rounded-lg text-center">
                <Users className="inline mb-1 text-purple-600" size={20} />
                <p className="text-2xl font-bold text-purple-700">{summary.member_failures}</p>
                <p className="text-sm text-purple-600">Members</p>
              </div>
              <div className="bg-orange-100 p-4 rounded-lg text-center">
                <Building className="inline mb-1 text-orange-600" size={20} />
                <p className="text-2xl font-bold text-orange-700">{summary.advertiser_failures}</p>
                <p className="text-sm text-orange-600">Advertisers</p>
              </div>
              <div className="bg-gray-100 p-4 rounded-lg text-center">
                <AlertTriangle className="inline mb-1 text-gray-600" size={20} />
                <p className="text-2xl font-bold text-gray-700">{summary.unknown_failures}</p>
                <p className="text-sm text-gray-600">Unknown</p>
              </div>
            </div>
          )}

          {/* Results Table */}
          {failures.length > 0 ? (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="failures-table">
                  <thead className="bg-red-600 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left">Channel</th>
                      <th className="px-4 py-3 text-left">Entity ID</th>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">Company</th>
                      <th className="px-4 py-3 text-left">Contact</th>
                      <th className="px-4 py-3 text-left">Magazine</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Error</th>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failures.map((failure, idx) => (
                      <tr key={failure.id || idx} className="border-b hover:bg-red-50">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                            failure.channel === 'Email' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {failure.channel === 'Email' ? (
                              <Mail size={12} className="mr-1" />
                            ) : (
                              <MessageCircle size={12} className="mr-1" />
                            )}
                            {failure.channel}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{failure.entity_id || '-'}</td>
                        <td className="px-4 py-3">{failure.entity_name || '-'}</td>
                        <td className="px-4 py-3 max-w-[150px] truncate" title={failure.entity_company}>
                          {failure.entity_company || '-'}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {failure.contact || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium ${
                            (failure.magazine === 'Aabkari Times' || failure.magazine === 'Aabkari Times ENG') 
                              ? 'text-blue-600' 
                              : failure.magazine === 'Sugar Times'
                                ? 'text-green-600'
                                : 'text-gray-500'
                          }`}>
                            {failure.magazine || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700">
                            {failure.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 max-w-[200px] text-xs text-red-600 truncate" title={failure.error}>
                          {failure.error || '-'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {formatDate(failure.date)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {failure.entity_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleNavigateToEntity(failure)}
                              title={`Edit ${failure.entity_type}`}
                              data-testid={`edit-${failure.entity_id}`}
                            >
                              <ExternalLink size={16} className="text-blue-600" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {failures.length >= 200 && (
                <div className="p-4 text-center text-sm text-gray-500 bg-gray-50">
                  Showing first 200 results. Narrow your search criteria to see more specific data.
                </div>
              )}
            </div>
          ) : !loading && (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <AlertTriangle size={64} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Failures Found</h3>
              <p className="text-gray-500">
                Great news! No delivery failures were found for the selected criteria.
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="mt-6 bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <div className="flex items-start">
              <AlertTriangle className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={20} />
              <div className="text-sm text-blue-700">
                <p className="font-semibold mb-1">How to use this report:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Click the <ExternalLink size={14} className="inline" /> icon to navigate to the member/advertiser record</li>
                  <li>Review and update invalid email addresses or WhatsApp numbers</li>
                  <li>Email failures often indicate bounced emails or invalid addresses</li>
                  <li>WhatsApp failures may indicate invalid numbers or users not on WhatsApp</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeliveryFailures;
